import * as THREE from 'three';

function noise2D(x: number, y: number, seed: number = 0): number {
  const n = Math.sin(x * 12.9898 + y * 78.233 + seed) * 43758.5453;
  return n - Math.floor(n);
}

function fbm(x: number, y: number, octaves: number = 4): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  for (let i = 0; i < octaves; i++) {
    value += amplitude * noise2D(x * frequency, y * frequency, i * 100);
    amplitude *= 0.5;
    frequency *= 2;
  }
  return value;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = THREE.MathUtils.clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

/** One texture repeat along the road = dash + gap (meters). Tune with dash UV repeat in World. */
export const ROAD_DASH_TEXTURE_PERIOD_M = 10;

export type RoadLineMarkingTextures = {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  bumpMap: THREE.CanvasTexture;
};

function configureRoadLineMap(tex: THREE.CanvasTexture, srgb: boolean): void {
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 16;
}

function canvasTextureFromImageData(
  img: ImageData,
  srgb: boolean,
): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = img.width;
  c.height = img.height;
  const ctx = c.getContext('2d')!;
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  configureRoadLineMap(tex, srgb);
  return tex;
}

/**
 * Thermoplastic dashed center line: high-res, irregular dash ends, glass-bead speckle,
 * micro-wear, and aligned roughness + bump so lighting reads as real paint on asphalt.
 */
export function createRoadDashMarkingTextures(): RoadLineMarkingTextures {
  const W = 2048;
  const H = 128;
  const mapImg = new ImageData(W, H);
  const roughImg = new ImageData(W, H);
  const bumpImg = new ImageData(W, H);
  const md = mapImg.data;
  const rd = roughImg.data;
  const bd = bumpImg.data;

  const featherIn = 0.006;
  const featherOut = 0.012;
  const rBase = 236;
  const gBase = 232;
  const bBase = 222;

  for (let y = 0; y < H; y++) {
    const nv = y / (H - 1);
    const vEdge = smoothstep(0, 0.1, nv) * smoothstep(1, 0.9, nv);
    const lateralCrown = Math.cos((nv - 0.5) * Math.PI) * 0.5 + 0.5;

    const dashEnd =
      0.54 +
      Math.sin(nv * Math.PI * 2 * 8) * 0.016 +
      (fbm(0.37, nv * 7.2, 4) - 0.5) * 0.022;

    for (let x = 0; x < W; x++) {
      const nu = x / W;
      const fadeIn = smoothstep(0, featherIn, nu);
      const fadeOut = 1 - smoothstep(dashEnd - featherOut, dashEnd, nu);
      let aLine = fadeIn * fadeOut;
      aLine = Math.max(0, Math.min(1, aLine));

      const wear = fbm(x * 0.003, nv * 2.4, 5);
      if (wear > 0.62) {
        aLine *= THREE.MathUtils.clamp(1 - (wear - 0.62) * 2.2, 0.4, 1);
      }

      const skid = fbm(x * 0.08, nv * 0.9, 2);
      const a = aLine * vEdge * (0.92 + skid * 0.08);

      const grain =
        (noise2D(x * 0.12, y * 0.45, 41) - 0.5) * 10 +
        (fbm(x / W, nv, 3) - 0.5) * 14;

      const beadNoise = noise2D(x * 0.41, y * 0.38, 19);
      const isBead = a > 0.15 && beadNoise > 0.985;
      const beadBoost = isBead ? 22 : 0;

      const rr = THREE.MathUtils.clamp(rBase + grain + beadBoost, 0, 255);
      const gg = THREE.MathUtils.clamp(gBase + grain * 0.96 + beadBoost * 0.95, 0, 255);
      const bb = THREE.MathUtils.clamp(bBase + grain * 0.88 + beadBoost * 0.82, 0, 255);

      let rough = 0.78 + lateralCrown * 0.12 + (noise2D(x * 0.02, y * 0.15, 3) - 0.5) * 0.08;
      if (isBead) rough = THREE.MathUtils.clamp(rough - 0.28, 0.28, 0.95);
      if (a < 0.05) rough = 0.96;

      const bumpCenter = lateralCrown * 0.55 + 0.25;
      const bumpGrain = (fbm(x * 0.015, nv * 2.1, 3) - 0.5) * 0.12;
      let bump = THREE.MathUtils.clamp(bumpCenter + bumpGrain, 0, 1);
      if (isBead) bump = THREE.MathUtils.clamp(bump + 0.14, 0, 1);

      const i = (y * W + x) * 4;
      md[i] = rr;
      md[i + 1] = gg;
      md[i + 2] = bb;
      md[i + 3] = Math.floor(a * 255);

      const rv = Math.floor(rough * 255);
      rd[i] = rv;
      rd[i + 1] = rv;
      rd[i + 2] = rv;
      rd[i + 3] = 255;

      const bv = Math.floor(bump * 255);
      bd[i] = bv;
      bd[i + 1] = bv;
      bd[i + 2] = bv;
      bd[i + 3] = 255;
    }
  }

  return {
    map: canvasTextureFromImageData(mapImg, true),
    roughnessMap: canvasTextureFromImageData(roughImg, false),
    bumpMap: canvasTextureFromImageData(bumpImg, false),
  };
}

/**
 * Solid edge line: same treatment as dashes minus alpha gaps (full strip).
 */
export function createRoadEdgeMarkingTextures(): RoadLineMarkingTextures {
  const W = 1024;
  const H = 56;
  const mapImg = new ImageData(W, H);
  const roughImg = new ImageData(W, H);
  const bumpImg = new ImageData(W, H);
  const md = mapImg.data;
  const rd = roughImg.data;
  const bd = bumpImg.data;

  for (let y = 0; y < H; y++) {
    const nv = y / (H - 1);
    const vy = Math.abs(nv - 0.5) * 2;
    const vEdge = smoothstep(0, 0.12, nv) * smoothstep(1, 0.88, nv);
    const profile = smoothstep(1, 0, vy) * 0.94 + 0.06;
    const lateralCrown = Math.cos((nv - 0.5) * Math.PI) * 0.5 + 0.5;

    for (let x = 0; x < W; x++) {
      const n =
        (noise2D(x * 0.28, y * 1.15, 19) - 0.5) * 18 +
        (fbm(x / W, nv, 3) - 0.5) * 14;
      const streak = Math.sin(x * 0.045 + y * 0.22) * 5;
      const joint = Math.sin(x * 0.012) * 0.5 + 0.5;

      const beadNoise = noise2D(x * 0.38, y * 0.35, 23);
      const isBead = beadNoise > 0.988;
      const beadBoost = isBead ? 18 : 0;

      const m = profile * vEdge * (0.9 + streak * 0.008 + (joint - 0.5) * 0.04);
      const rr = THREE.MathUtils.clamp(228 + n * m + beadBoost, 188, 252);
      const gg = THREE.MathUtils.clamp(224 + n * m * 0.97 + beadBoost * 0.95, 184, 250);
      const bb = THREE.MathUtils.clamp(214 + n * m * 0.9 + beadBoost * 0.85, 176, 242);

      let rough = 0.8 + lateralCrown * 0.1 + (noise2D(x * 0.018, y * 0.12, 5) - 0.5) * 0.07;
      if (isBead) rough = THREE.MathUtils.clamp(rough - 0.26, 0.3, 0.95);

      const bumpCenter = lateralCrown * 0.52 + 0.28;
      const bumpGrain = (fbm(x * 0.012, nv * 2.4, 3) - 0.5) * 0.1;
      let bump = THREE.MathUtils.clamp(bumpCenter + bumpGrain, 0, 1);
      if (isBead) bump = THREE.MathUtils.clamp(bump + 0.12, 0, 1);

      const i = (y * W + x) * 4;
      md[i] = rr;
      md[i + 1] = gg;
      md[i + 2] = bb;
      md[i + 3] = 255;

      const rv = Math.floor(rough * 255);
      rd[i] = rv;
      rd[i + 1] = rv;
      rd[i + 2] = rv;
      rd[i + 3] = 255;

      const bv = Math.floor(bump * 255);
      bd[i] = bv;
      bd[i + 1] = bv;
      bd[i + 2] = bv;
      bd[i + 3] = 255;
    }
  }

  return {
    map: canvasTextureFromImageData(mapImg, true),
    roughnessMap: canvasTextureFromImageData(roughImg, false),
    bumpMap: canvasTextureFromImageData(bumpImg, false),
  };
}

/** @deprecated Prefer createRoadDashMarkingTextures — kept for single-map call sites. */
export function createRoadDashStripeTexture(): THREE.CanvasTexture {
  return createRoadDashMarkingTextures().map;
}

/** @deprecated Prefer createRoadEdgeMarkingTextures */
export function createRoadEdgeLineTexture(): THREE.CanvasTexture {
  return createRoadEdgeMarkingTextures().map;
}

export function createAsphaltRoughnessTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  const n = 256;
  c.width = n;
  c.height = n;
  const g = c.getContext('2d')!;
  const img = g.createImageData(n, n);
  const data = img.data;
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const i = (y * n + x) * 4;
      const nx = x / n;
      const ny = y / n;
      const coarse = Math.sin(nx * 40) * Math.cos(ny * 37) * 0.08;
      const fine = (Math.random() - 0.5) * 0.12;
      const stain = Math.sin(nx * 6.2 + ny * 5.1) * 0.06;
      let v = 0.52 + coarse + fine + stain;
      v = THREE.MathUtils.clamp(v, 0.35, 0.92);
      const b = Math.floor(v * 255);
      data[i] = b;
      data[i + 1] = b;
      data[i + 2] = b;
      data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.anisotropy = 8;
  return tex;
}

export function createRealisticAsphaltTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  const size = 1024;
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;

  ctx.fillStyle = '#3a3a3a';
  ctx.fillRect(0, 0, size, size);

  const img = ctx.getImageData(0, 0, size, size);
  const data = img.data;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const nx = x / size;
      const ny = y / size;

      const coarseAggregate = fbm(nx * 120, ny * 120, 4) * 25;
      const fineAggregate = fbm(nx * 300, ny * 300, 2) * 12;
      const microDetail = (noise2D(x * 0.8, y * 0.8) - 0.5) * 18;
      const largeVariation = fbm(nx * 6, ny * 6, 2) * 20;

      let hasCrack = false;
      const crackNoise = fbm(nx * 12 + 0.5, ny * 12, 5);
      if (crackNoise > 0.68 && crackNoise < 0.72) {
        hasCrack = true;
      }

      let hasPatch = false;
      const patchNoise = fbm(nx * 3, ny * 3, 2);
      if (patchNoise > 0.72) {
        hasPatch = true;
      }

      let hasOilStain = false;
      const oilNoise = fbm(nx * 5 + 100, ny * 5 + 100, 2);
      if (oilNoise > 0.78) {
        hasOilStain = true;
      }

      let baseGray = 58;

      if (hasPatch) {
        baseGray = 72 + fbm(nx * 15, ny * 15, 2) * 20;
      }

      if (hasOilStain) {
        baseGray = 35 + fbm(nx * 25, ny * 25, 2) * 15;
      }

      if (hasCrack) {
        baseGray = 20;
      }

      const variation = coarseAggregate + fineAggregate + microDetail + largeVariation;
      const finalGray = THREE.MathUtils.clamp(baseGray + variation, 30, 95);

      data[i] = finalGray;
      data[i + 1] = finalGray;
      data[i + 2] = finalGray + (hasOilStain ? -8 : 2);
      data[i + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);

  ctx.strokeStyle = '#252525';
  ctx.lineWidth = 2;
  for (let i = 0; i < 15; i++) {
    const startX = Math.random() * size;
    const startY = Math.random() * size;
    ctx.beginPath();
    ctx.moveTo(startX, startY);
    let cx = startX;
    let cy = startY;
    for (let j = 0; j < 8; j++) {
      cx += (Math.random() - 0.5) * 60;
      cy += (Math.random() - 0.5) * 60;
      ctx.lineTo(cx, cy);
    }
    ctx.stroke();
  }

  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 16;
  return tex;
}

export function createAsphaltNormalMap(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  const size = 512;
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;

  ctx.fillStyle = '#8080ff';
  ctx.fillRect(0, 0, size, size);

  const img = ctx.getImageData(0, 0, size, size);
  const data = img.data;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const nx = x / size;
      const ny = y / size;

      const bumpX = (fbm((nx + 0.01) * 60, ny * 60, 3) - fbm((nx - 0.01) * 60, ny * 60, 3)) * 2;
      const bumpY = (fbm(nx * 60, (ny + 0.01) * 60, 3) - fbm(nx * 60, (ny - 0.01) * 60, 3)) * 2;

      data[i] = THREE.MathUtils.clamp(128 + bumpX * 60, 0, 255);
      data[i + 1] = THREE.MathUtils.clamp(128 + bumpY * 60, 0, 255);
      data[i + 2] = 255;
      data[i + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.anisotropy = 8;
  return tex;
}

export function createSidewalkTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  const size = 512;
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;

  ctx.fillStyle = '#707070';
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle = '#505050';
  ctx.lineWidth = 3;

  const slabSize = size / 4;
  for (let y = 0; y <= size; y += slabSize) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(size, y);
    ctx.stroke();
  }
  for (let x = 0; x <= size; x += slabSize) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, size);
    ctx.stroke();
  }

  const img = ctx.getImageData(0, 0, size, size);
  const data = img.data;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const nx = x / size;
      const ny = y / size;

      const slabX = Math.floor(x / slabSize);
      const slabY = Math.floor(y / slabSize);
      const slabSeed = slabX * 7 + slabY * 13;
      const slabTint = (noise2D(slabX, slabY, slabSeed) - 0.5) * 20;

      const microNoise = (noise2D(x * 0.3, y * 0.3) - 0.5) * 15;
      const aggregate = fbm(nx * 40, ny * 40, 2) * 10;

      const crackChance = noise2D(nx * 8, ny * 8, 42);
      let crack = 0;
      if (crackChance > 0.92) {
        const crackLine = fbm(nx * 30, ny * 30, 3);
        if (crackLine > 0.5 && crackLine < 0.55) {
          crack = -30;
        }
      }

      data[i] = THREE.MathUtils.clamp(data[i] + slabTint + microNoise + aggregate + crack, 50, 140);
      data[i + 1] = THREE.MathUtils.clamp(data[i + 1] + slabTint + microNoise + aggregate + crack, 50, 140);
      data[i + 2] = THREE.MathUtils.clamp(data[i + 2] + slabTint + microNoise + aggregate + crack, 50, 140);
    }
  }

  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

export function createBuildingFacadeTexture(
  windowRows: number = 8,
  windowCols: number = 6,
  style: 'modern' | 'classic' | 'industrial' = 'modern',
): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  const size = 512;
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;

  let baseColor: string;
  let windowColor: string;
  let frameColor: string;

  switch (style) {
    case 'modern':
      baseColor = '#4a5568';
      windowColor = '#1a202c';
      frameColor = '#2d3748';
      break;
    case 'classic':
      baseColor = '#a0826d';
      windowColor = '#2d3748';
      frameColor = '#8b7355';
      break;
    case 'industrial':
      baseColor = '#5a5a5a';
      windowColor = '#1a1a2e';
      frameColor = '#404040';
      break;
  }

  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, size, size);

  const windowW = (size * 0.7) / windowCols;
  const windowH = (size * 0.8) / windowRows;
  const marginX = (size - windowCols * windowW) / 2;
  const marginY = (size - windowRows * windowH) / 2;

  for (let row = 0; row < windowRows; row++) {
    for (let col = 0; col < windowCols; col++) {
      const x = marginX + col * windowW + windowW * 0.15;
      const y = marginY + row * windowH + windowH * 0.15;
      const w = windowW * 0.7;
      const h = windowH * 0.7;

      ctx.fillStyle = frameColor;
      ctx.fillRect(x - 2, y - 2, w + 4, h + 4);

      const isLit = Math.random() > 0.6;
      if (isLit) {
        const warmth = Math.random();
        const r = Math.floor(255 * (0.8 + warmth * 0.2));
        const g = Math.floor(200 * (0.7 + warmth * 0.3));
        const b = Math.floor(150 * (0.5 + warmth * 0.3));
        ctx.fillStyle = `rgb(${r},${g},${b})`;
      } else {
        ctx.fillStyle = windowColor;
      }
      ctx.fillRect(x, y, w, h);

      if (style === 'modern') {
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.fillRect(x, y, w * 0.3, h);
      }
    }
  }

  const img = ctx.getImageData(0, 0, size, size);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    const noise = (Math.random() - 0.5) * 8;
    data[i] = THREE.MathUtils.clamp(data[i] + noise, 0, 255);
    data[i + 1] = THREE.MathUtils.clamp(data[i + 1] + noise, 0, 255);
    data[i + 2] = THREE.MathUtils.clamp(data[i + 2] + noise, 0, 255);
  }
  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

export function createRoadMarkingsTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  const size = 512;
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;

  ctx.fillStyle = 'rgba(0,0,0,0)';
  ctx.clearRect(0, 0, size, size);

  ctx.fillStyle = '#e8e8e8';
  ctx.strokeStyle = '#e8e8e8';

  const dashLength = 60;
  const gapLength = 40;
  const lineWidth = 8;

  ctx.lineWidth = lineWidth;
  ctx.setLineDash([dashLength, gapLength]);
  ctx.beginPath();
  ctx.moveTo(size / 2, 0);
  ctx.lineTo(size / 2, size);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(20, 0);
  ctx.lineTo(20, size);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(size - 20, 0);
  ctx.lineTo(size - 20, size);
  ctx.stroke();

  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

export function createCrossWalkTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  const size = 256;
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;

  ctx.fillStyle = 'rgba(0,0,0,0)';
  ctx.clearRect(0, 0, size, size);

  ctx.fillStyle = '#e0e0e0';

  const stripeCount = 8;
  const stripeWidth = size / (stripeCount * 2);

  for (let i = 0; i < stripeCount; i++) {
    const x = i * stripeWidth * 2;
    ctx.fillRect(x, 0, stripeWidth, size);
  }

  const img = ctx.getImageData(0, 0, size, size);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] > 0) {
      const wear = (Math.random() - 0.5) * 30;
      data[i] = THREE.MathUtils.clamp(data[i] + wear, 180, 255);
      data[i + 1] = THREE.MathUtils.clamp(data[i + 1] + wear, 180, 255);
      data[i + 2] = THREE.MathUtils.clamp(data[i + 2] + wear, 180, 255);
      data[i + 3] = THREE.MathUtils.clamp(data[i + 3] - Math.random() * 20, 200, 255);
    }
  }
  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

export function createManholeTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  const size = 128;
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;

  ctx.fillStyle = 'rgba(0,0,0,0)';
  ctx.clearRect(0, 0, size, size);

  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.4;

  ctx.fillStyle = '#3a3a3a';
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = '#2a2a2a';
  ctx.lineWidth = 3;
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(angle) * radius * 0.3, cy + Math.sin(angle) * radius * 0.3);
    ctx.lineTo(cx + Math.cos(angle) * radius * 0.9, cy + Math.sin(angle) * radius * 0.9);
    ctx.stroke();
  }

  ctx.strokeStyle = '#4a4a4a';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, radius * 0.7, 0, Math.PI * 2);
  ctx.stroke();

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
